import { useEffect } from 'react';

import { Autocomplete, FormControl, MenuItem, TextField, Typography } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { filterHelper } from 'common/components/sharedCustomComponents/Common/helpers';
import { CustomPaperComponent, DISPLAYED_ROWS_COUNT } from 'common/components/sharedCustomComponents/CustomPaperComponent';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchClaims } from 'common/services/api/Claims/Claims.thunk';
import { Claim } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { editDrawerAutocompleteStyles } from 'common/utils/StyleHelpers';
import { selectClaims } from 'features/claims/claimsSlice';
import { useSnackbar } from 'notistack';

export function ClaimNameDropDown(props: Readonly<{
        tenantId: string,
        value: Claim,
        onChange: (e: unknown, claim: Claim) => void,
        hasError: boolean,
    }>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        dispatch(fetchClaims({ tenantId: props.tenantId }))
            .unwrap()
            .catch(error => {
                handleListFetchError(extractStatusCode(error.message), 'Claim', enqueueSnackbar);
            });
    }, []);

    const claims = useAppSelector(selectClaims);

    return (
        <FormControl>
            <Autocomplete
                value={props.value}
                onChange={props.onChange}
                disableClearable
                sx={editDrawerAutocompleteStyles(props.hasError)}
                handleHomeEndKeys={false}
                filterOptions={filterHelper}
                PaperComponent={CustomPaperComponent}
                options={[{ id: '', name: '', tenantId: '', claimDataTypeId: '', claimDataTypeName: '' }, ...claims]}
                getOptionLabel={claim => claim.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => <TextField {...params} label="Tenant Claim Search" />}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                renderOption={({ key: _, ...params }, claim) => {
                    if (params.id && Number(params.id.split('-').pop()) > DISPLAYED_ROWS_COUNT) {
                        return null;
                    }
                    const isPlaceholder = !claim.id;
                    return (
                        <MenuItem
                            key={claim.id}
                            {...params}
                            value={claim.id}
                            style={isPlaceholder ? { display: 'none' } : { display: 'block' } }
                            disabled={isPlaceholder}
                        >
                            <Typography>
                                {claim.name}
                            </Typography>
                            <Typography
                                variant="body1"
                                gutterBottom
                                color="info.light"
                                noWrap
                            >
                                {`Claim Data Type: ${claim.claimDataTypeName}`}
                            </Typography>
                        </MenuItem>
                    );
                }}
            />
        </FormControl>
    );
}
